import type { StoryDanger } from '../../../packages/contracts/src/index.ts';
import type { Point } from '../../../packages/view-phaser/src/painted-renderer.ts';
import type { Area } from './painted-world.ts';

// Things in the painting you act on: objects from the area recipes, the people the story
// has here, the ways out, and you (for a wound). Each is a button over the painting, so a
// mouse, a keyboard and a harness all reach it the same way.

/** A marker button's live layout state, written once a frame by {@link WorldMarkers.place}. */
export interface MarkerSlot { node: HTMLElement; left: number; top: number; edge: boolean; scale: number; vside: string; lift: number; tagW: number; tagH: number; tagX: number }

/** A HUD panel over the world, in the marker layer's pixels: names keep clear of every one. */
export interface ClearRect { left: number; top: number; right: number; bottom: number }

/** An area recipe's `objects` entry: an action's place on the painting (docs/plans/world-first-2026-09-30.md). */
export interface WorldObject { id: string; beatId: string; name: string; at: Point; radius: number; actions: readonly string[]; look?: string }
export interface Verb { id: string; label: string; enabled: boolean; reason: string | null; costCuts?: number; danger?: StoryDanger | undefined }
export interface Marker {
  key: string; kind: 'object' | 'person' | 'exit'; id: string; name: string; at: Point; radius: number;
  verbs: readonly Verb[]; hint: string; look?: string; enabled?: boolean;
  /** Drawn at this actor's figure instead of `at`. */
  follow?: string;
}

export const objectsOf = (area: Area, beatId: string): readonly WorldObject[] => (area.objects ?? []).filter(object => object.beatId === beatId);

/** Holding Alt shows the name of everything you can use in view. */
export function holdAltLabels(): void {
  const labels = (on: boolean): void => { document.documentElement.toggleAttribute('data-world-labels', on); };
  document.addEventListener('keydown', event => { if (event.key === 'Alt') { event.preventDefault(); labels(true); } });
  document.addEventListener('keyup', event => { if (event.key === 'Alt') { event.preventDefault(); labels(false); } });
  window.addEventListener('blur', () => labels(false));
}

/**
 * The beat's objects with the verbs the story offers now. An offered action no object owns
 * (a recipe not yet placed, a wound to dress) still has a place: at your own figure for a
 * wound, otherwise at the beat's point. The coverage test keeps the second case empty.
 */
export function objectMarkers(area: Area, beatId: string, interactions: readonly Verb[], at: Point, placeName: string): Marker[] {
  const owned = new Set<string>();
  const markers: Marker[] = objectsOf(area, beatId).flatMap(object => {
    const verbs = interactions.filter(verb => object.actions.includes(verb.id));
    verbs.forEach(verb => owned.add(verb.id));
    if (!verbs.length && !object.look) return [];
    return [{ key: `object:${object.id}`, kind: 'object' as const, id: object.id, name: object.name, at: object.at, radius: object.radius, verbs, hint: hint(verbs, object.look), ...(object.look ? { look: object.look } : {}) }];
  });
  const wounds = interactions.filter(verb => verb.id.startsWith('treat:'));
  if (wounds.length) markers.push({ key: 'object:self', kind: 'object', id: 'self', name: 'You', at, radius: 26, verbs: wounds, hint: hint(wounds), follow: 'person:pc' });
  const loose = interactions.filter(verb => !owned.has(verb.id) && !verb.id.startsWith('treat:'));
  if (loose.length) markers.push({ key: 'object:here', kind: 'object', id: 'here', name: placeName, at, radius: 30, verbs: loose, hint: hint(loose) });
  return markers;
}

/** One free verb is simply done; several, a price to confirm, or only a reason, open the menu. */
export const opensMenu = (marker: Marker): boolean => marker.verbs.length !== 1 || !marker.verbs[0]!.enabled || !!marker.verbs[0]!.costCuts || !!marker.verbs[0]!.danger;

const hint = (verbs: readonly Verb[], look?: string): string => {
  const first = verbs.find(verb => verb.enabled) ?? verbs[0];
  if (!first) return look ? 'Look' : '';
  return first.enabled ? `${first.danger ? 'Danger · ' : ''}${first.label}` + (verbs.length > 1 ? ` · ${verbs.length - 1} more` : '') : first.reason ?? first.label;
};

const escape = (value: string): string => value.replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]!);
const DATA = { object: 'object', person: 'talk', exit: 'visit' } as const;

/**
 * Where a name hangs so it clears every HUD panel: which side of its button, how far
 * along the button, how far out. Pure geometry — `place` applies it. Buttons never move
 * for panels: only their names flip below, slide along, or lift at need. With no panel
 * in the way this returns the centered, unlifted hang, exactly as before.
 */
export function placeTag(top: number, left: number, tagW: number, tagH: number, below: boolean, view: { left: number; top: number; right: number; bottom: number }, clear: readonly ClearRect[]): { below: boolean; tagLeft: number; lift: number } {
  const MARGIN = 6;
  const box = (side: boolean, x: number, lift: number): ClearRect => side
    ? { left: x, top: top + 4 + lift, right: x + tagW, bottom: top + 4 + lift + tagH }
    : { left: x, top: top - 4 - lift - tagH, right: x + tagW, bottom: top - 4 - lift };
  const hits = (rect: ClearRect): ClearRect | undefined => clear.find(panel => rect.left < panel.right + MARGIN && rect.right > panel.left - MARGIN && rect.top < panel.bottom + MARGIN && rect.bottom > panel.top - MARGIN);
  const centered = (): number => tagW + 8 >= view.right - view.left
    ? view.left + 4
    : Math.round(Math.min(view.right - 4 - tagW, Math.max(view.left + 4, left - tagW / 2)));
  let side = below, x = centered(), lift = 0;
  // A panel over the usual side: hang below the button instead, when that is clear.
  if (hits(box(side, x, lift)) && !hits(box(!side, x, lift))) side = !side;
  let panel = hits(box(side, x, lift)), guard = 0;
  while (panel && guard++ < 8) {
    // Slide clear along the button, away from the panel's middle; only when there is
    // nowhere to slide does the name drop further from its button.
    const middle = (panel.left + panel.right) / 2, center = x + tagW / 2;
    const slid = center < middle
      ? Math.max(view.left + 4, panel.left - MARGIN - tagW)
      : Math.min(view.right - 4 - tagW, panel.right + MARGIN);
    if (slid === x) lift += 34; else x = slid;
    panel = hits(box(side, x, lift));
  }
  return { below: side, tagLeft: Math.round(x), lift };
}
/**
 * How far a name lifts above (or below) its button to clear the names already
 * placed nearby. Pure geometry — `place` applies it and records each placed
 * name's measured height so taller names keep apart by more than one line.
 */
export interface TakenTag { left: number; ay: number; h: number }
export function staggerLift(taken: readonly TakenTag[], top: number, left: number, below: boolean, startLift: number, tagH: number): number {
  // An unmeasured name counts as one line, exactly as before.
  const measured = (h: number): number => h > 0 ? h : 34;
  const want = measured(tagH);
  let lift = startLift;
  const anchor = (): number => below ? top + lift : top - lift;
  while (taken.some(tag => Math.abs(tag.left - left) < 120 && Math.abs(tag.ay - anchor()) < (measured(tag.h) + want) / 2)) lift += Math.max(34, want);
  return lift;
}

/** How near, in painted pixels, an object with something to do glints once. */
const GLINT_PIXELS = 150;

/** The buttons over the painting, the verb menu, and the hold-Alt labels. */
export class WorldMarkers {
  #layer: HTMLElement;
  #menu: HTMLElement;
  #markers: Marker[] = [];
  #nodes = new Map<string, MarkerSlot>();
  #signature = '';
  #seen = new Set<string>();
  #menuKey: string | null = null;
  constructor(layer: HTMLElement, menu: HTMLElement) {
    this.#layer = layer; this.#menu = menu;
  }
  get markers(): readonly Marker[] { return this.#markers; }
  get menuOpen(): string | null { return this.#menuKey; }
  find(key: string): Marker | undefined { return this.#markers.find(marker => marker.key === key); }
  /** One chosen lead stays named, including at the screen edge. No new marker or hit target. */
  guide(key: string | null): void {
    for (const [id, slot] of this.#nodes) slot.node.classList.toggle('guided', id === key);
  }
  /** Rebuild the buttons when what they offer changes; `hidden` while a fight or a page holds the world. */
  render(markers: Marker[], hidden: boolean): void {
    // Screen order for Tab: back to front, left to right.
    this.#markers = [...markers].sort((a, b) => a.at[1] - b.at[1] || a.at[0] - b.at[0]);
    this.#layer.toggleAttribute('data-held', hidden);
    if (hidden) this.close();
    const signature = JSON.stringify(this.#markers.map(({ key, name, hint, verbs, enabled }) => [key, name, hint, verbs.map(verb => [verb.enabled, !!verb.danger]), enabled, hidden]));
    if (signature === this.#signature) return;
    this.#signature = signature;
    this.#layer.innerHTML = this.#markers.map(marker => {
      const actions = marker.verbs.map(verb => verb.id).join(' ');
      const live = marker.kind !== 'object' ? marker.enabled !== false : marker.verbs.some(verb => verb.enabled);
      return `<button type="button" class="mark ${marker.kind}${marker.verbs.some(verb => verb.enabled && verb.danger) ? ' dangerous' : ''}${live ? '' : ' idle'}" data-${DATA[marker.kind]}="${escape(marker.id)}"${actions ? ` data-actions="${escape(actions)}"` : ''}${marker.kind === 'object' && marker.verbs.length && opensMenu(marker) ? ' data-menu' : ''} style="--r:${marker.radius}" ${hidden ? 'hidden tabindex="-1"' : ''} aria-label="${escape(`${marker.name}. ${marker.hint}`)}"><span class="tag"><strong>${escape(marker.name)}</strong>${marker.hint ? `<small>${escape(marker.hint)}</small>` : ''}</span></button>`;
    }).join('');
    this.#nodes.clear();
    Array.from(this.#layer.querySelectorAll<HTMLElement>('.mark'), (node, i) => this.#nodes.set(this.#markers[i]!.key, { node, left: NaN, top: NaN, edge: false, scale: NaN, vside: '', lift: 0, tagW: NaN, tagH: NaN, tagX: NaN }));
    if (this.#menuKey && !this.find(this.#menuKey)) this.close();
  }
  /**
   * Once a frame: set each button over its thing. `project` maps a painted point to the
   * layer's pixels, and `scale` is layer pixels per painted pixel. A thing out of view waits
   * at the nearest edge, so the keyboard and a click can still reach it. `clear` is every
   * HUD panel over the world in the layer's pixels: buttons never move for panels, but a
   * name that would hide under one hangs on the other side of its button or slides clear.
   */
  place(project: (point: Point) => Point, figures: ReadonlyMap<string, Point>, scale: number, view: { left: number; top: number; right: number; bottom: number }, pc: Point | null, clear: readonly ClearRect[] = []): void {
    const pad = 22, topPad = pad + 60;
    const spots: { marker: Marker; slot: MarkerSlot; x: number; y: number; left: number; top: number; edge: boolean }[] = [];
    for (const marker of this.#markers) {
      const slot = this.#nodes.get(marker.key);
      if (!slot) continue;
      const figure = marker.follow ?? (marker.kind === 'person' ? marker.id : undefined), drawn = figure ? figures.get(figure) : undefined;
      // A person's button covers the body, not the feet.
      const [x, y] = project(drawn ? [drawn[0], drawn[1] - (marker.kind === 'person' || marker.follow ? 34 : 0)] : marker.at);
      const left = Math.round(Math.min(view.right - pad, Math.max(view.left + pad, x))), top = Math.round(Math.min(view.bottom - pad, Math.max(view.top + topPad, y)));
      spots.push({ marker, slot, x, y, left, top, edge: left !== Math.round(x) || top !== Math.round(y) });
    }
    // Markers waiting at the same edge fan out along it so no two buttons cover each other.
    this.#spread(spots, scale, view, pad, topPad);
    // Names keep apart the same way: a name that would cover a nearer one's rises above it.
    const tags: TakenTag[] = [];
    for (const spot of spots) {
      const { marker, slot, left, top } = spot;
      if (left !== slot.left || top !== slot.top) { slot.node.style.left = `${left}px`; slot.node.style.top = `${top}px`; slot.left = left; slot.top = top; }
      if (spot.edge !== slot.edge) { slot.node.classList.toggle('edge', spot.edge); slot.edge = spot.edge; }
      if (scale !== slot.scale) { slot.node.style.setProperty('--s', String(scale)); slot.scale = scale; }
      // Near the top of the view the name hangs below the button instead of being cut off.
      let vside = top < view.top + 130 ? 't' : '';
      // The name stays inside the view: it slides along the button rather than clipping at the edge.
      if (Number.isNaN(slot.tagW)) {
        const box = slot.node.querySelector('.tag')?.getBoundingClientRect();
        slot.tagW = box?.width ?? 0; slot.tagH = box?.height ?? 0;
      }
      let lift = 0, tagLeft = 0;
      if (slot.tagW > 0) {
        const placed = placeTag(top, left, slot.tagW, slot.tagH, vside === 't', view, clear);
        vside = placed.below ? 't' : ''; tagLeft = placed.tagLeft; lift = placed.lift;
      }
      if (vside !== slot.vside) { if (vside) slot.node.dataset.vside = vside; else delete slot.node.dataset.vside; slot.vside = vside; }
      lift = staggerLift(tags, top, left, vside === 't', lift, slot.tagH);
      tags.push({ left, ay: vside ? top + lift : top - lift, h: slot.tagH });
      if (lift !== slot.lift) { slot.node.style.setProperty('--lift', `${lift}px`); slot.lift = lift; }
      if (slot.tagW > 0) {
        if (tagLeft - left !== slot.tagX) { slot.node.style.setProperty('--tagx', `${tagLeft - left}px`); slot.tagX = tagLeft - left; }
      }
      if (this.#menuKey === marker.key) { this.#menu.style.left = `${left}px`; this.#menu.style.top = `${top}px`; }
      // Discovery: the first time you come near something with a thing to do, it glints once.
      const glint = `${marker.key}|${marker.verbs.filter(verb => verb.enabled).map(verb => verb.id).join()}`;
      if (pc && marker.kind === 'object' && !marker.follow && marker.verbs.some(verb => verb.enabled) && !this.#seen.has(glint) && Math.hypot(pc[0] - marker.at[0], pc[1] - marker.at[1]) < GLINT_PIXELS) {
        this.#seen.add(glint);
        slot.node.classList.remove('glint'); void slot.node.offsetWidth; slot.node.classList.add('glint');
        const node = slot.node; window.setTimeout(() => node.classList.remove('glint'), 2600);
      }
    }
  }
  /** A marker the camera cannot show waits at the nearest edge; several at one edge spread out. */
  #spread(spots: { marker: Marker; x: number; y: number; left: number; top: number; edge: boolean }[], scale: number, view: { left: number; top: number; right: number; bottom: number }, pad: number, topPad: number): void {
    const GAP = 10;
    const size = (marker: Marker): { w: number; h: number } => {
      if (marker.kind === 'person') return { w: Math.max(28, 24 * scale), h: 55.2 * scale };
      const d = Math.max(28, 2 * marker.radius * scale);
      return { w: d, h: d };
    };
    const groups = new Map<string, { vertical: boolean; lo: number; hi: number; items: { spot: (typeof spots)[number]; c: number; h: number; order: number; fixed: boolean }[] }>();
    spots.forEach((spot, order) => {
      if (!spot.edge) return;
      const l = spot.x < view.left + pad, r = spot.x > view.right - pad, t = spot.y < view.top + topPad, b = spot.y > view.bottom - pad;
      const ox = l ? view.left + pad - spot.x : r ? spot.x - (view.right - pad) : 0;
      const oy = t ? view.top + topPad - spot.y : b ? spot.y - (view.bottom - pad) : 0;
      const vertical = (l || r) && (!(t && b) && (!(t || b) || ox >= oy));
      const key = l && vertical ? 'L' : r && vertical ? 'R' : t ? 'T' : 'B';
      let group = groups.get(key);
      if (!group) {
        group = vertical
          ? { vertical, lo: view.top + topPad, hi: view.bottom - pad, items: [] }
          : { vertical, lo: view.left + pad, hi: view.right - pad, items: [] };
        groups.set(key, group);
      }
      const s = size(spot.marker);
      group.items.push({ spot, c: vertical ? spot.top : spot.left, h: (vertical ? s.h : s.w) / 2, order, fixed: false });
    });
    for (const group of groups.values()) {
      const line = group.vertical ? group.items[0]!.spot.left : group.items[0]!.spot.top;
      for (const [index, spot] of spots.entries()) {
        if (spot.edge) continue;
        const s = size(spot.marker);
        const along = group.vertical ? spot.top : spot.left, across = group.vertical ? spot.left : spot.top;
        if (Math.abs(across - line) < (group.vertical ? s.w : s.h) / 2 + 100) {
          group.items.push({ spot, c: along, h: (group.vertical ? s.h : s.w) / 2, order: index, fixed: true });
        }
      }
      group.items.sort((a, b) => a.c - b.c || a.order - b.order);
      const sweep = (): number => {
        let cursor = group.lo;
        for (const item of group.items) {
          if (!item.fixed) item.c = Math.max(item.c, cursor + item.h);
          cursor = Math.max(cursor, item.c + item.h + GAP);
        }
        return cursor - GAP;
      };
      if (sweep() > group.hi) {
        const over = sweep() - group.hi;
        for (const item of group.items) if (!item.fixed) item.c -= over;
        sweep();
      }
      for (const item of group.items) {
        if (item.fixed) continue;
        const c = Math.round(item.c);
        if (group.vertical) item.spot.top = c; else item.spot.left = c;
      }
    }
  }
  /** The small verb menu over a thing; `body` is its list of verb buttons. */
  open(key: string, body: string): void {
    const marker = this.find(key);
    if (!marker) return;
    this.#menuKey = key;
    this.#menu.innerHTML = `<p class="menu-name">${escape(marker.name)}</p>${marker.look ? `<p class="menu-look">${escape(marker.look)}</p>` : ''}${body}`;
    this.#menu.hidden = false;
    const slot = this.#nodes.get(key);
    if (slot) { this.#menu.style.left = `${slot.left}px`; this.#menu.style.top = `${slot.top}px`; }
    this.#menu.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
  }
  close(): boolean {
    if (!this.#menuKey) return false;
    const key = this.#menuKey;
    this.#menuKey = null; this.#menu.hidden = true; this.#menu.replaceChildren();
    this.#nodes.get(key)?.node.focus({ preventScroll: true });
    return true;
  }
}
